// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utility for determining asset ownership status.
 * This module provides functionality to check whether an asset
 * is currently owned by the manufacturer or has been assigned
 * to a customer.
 */
import { Asset } from '@/asset/api/types';

/**
 * Determines if an asset is currently owned by the manufacturer.
 * An asset is considered manufacturer-owned in two cases:
 * 1. The asset has no ownership (null ownership)
 * 2. The asset has an ownership but no customer (null customer)
 *
 * Note: This ownership check is distinct from project assignments.
 * While an asset may be assigned to a project, its ownership is
 * determined solely by the customer ownership status.
 *
 * @param asset - Asset or asset-like object with ownership property
 * @returns True if the asset is manufacturer-owned, false if customer-owned
 */
export function isAssetOwned(asset: Pick<Asset, 'ownership'>): boolean {
  // An asset's ownership is represented by the 'ownership' property. An asset
  // without an ownership is considered owned by the manufacturer. An asset
  // with an ownership that has no customer is also considered owned by the
  // manufacturer.
  //
  // The 'ownership' property is notably different from an asset's project
  // assignment.
  if (asset.ownership === null) {
    return true;
  }

  return asset.ownership.customer === null;
}
